import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { X } from "lucide-react"; // Import X icon for filter removal
import Button from "@components/Button";
import PropTypes from "prop-types";
import { IoChevronForward } from "react-icons/io5";

export default function Blogs({ numberOfPosts, showMetaTags = true }) {
	const [posts, setPosts] = useState([]);
	const [filteredPosts, setFilteredPosts] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [searchParams, setSearchParams] = useSearchParams();

	// Get the tag filter from URL params
	const activeTag = searchParams.get("tag");

	const WORDPRESS_API_URL =
		"https://blogs.steersmartdriving.com.au/wp-json/wp/v2";

	useEffect(() => {
		fetchPosts();
	}, []);

	// Filter posts when activeTag changes
	useEffect(() => {
		if (activeTag && posts.length > 0) {
			const filtered = posts.filter((post) => {
				const postTags = getTags(post);
				return postTags.some(
					(tag) => tag.name.toLowerCase() === activeTag.toLowerCase()
				);
			});
			setFilteredPosts(filtered);
		} else {
			setFilteredPosts(posts);
		}
	}, [activeTag, posts]);

	const fetchPosts = async () => {
		try {
			setLoading(true);
			// Fetch posts with embedded media and author info
			const limit = !activeTag && numberOfPosts ? numberOfPosts : 50;
			const response = await fetch(
				`${WORDPRESS_API_URL}/posts?_embed&per_page=${limit}`
			);

			if (!response.ok) {
				throw new Error("Failed to fetch posts");
			}

			const data = await response.json();
			setPosts(data);
		} catch (err) {
			setError(err.message);
		} finally {
			setLoading(false);
		}
	};

	const getTags = (post) => {
		return post._embedded?.["wp:term"]?.[1] || [];
	};

	const stripHTML = (html) => {
		const div = document.createElement("div");
		div.innerHTML = html;
		return div.textContent || div.innerText || "";
	};

	const formatDate = (dateString) => {
		return new Date(dateString).toLocaleDateString("en-US", {
			year: "numeric",
			month: "long",
			day: "numeric",
		});
	};

	const getFeaturedImage = (post) => {
		const featuredMedia = post._embedded?.["wp:featuredmedia"]?.[0];
		if (!featuredMedia) return null;

		const sizes = featuredMedia.media_details?.sizes;
		if (sizes) {
			// Prefer medium_large (typically ~768px wide) for card views
			return (
				sizes.medium_large?.source_url ||
				sizes.large?.source_url ||
				sizes.medium?.source_url ||
				featuredMedia.source_url
			);
		}
		return featuredMedia.source_url;
	};

	const clearTagFilter = () => {
		setSearchParams({});
	};

	// Animation variants
	const containerVariants = {
		hidden: { opacity: 0 },
		visible: {
			opacity: 1,
			transition: {
				staggerChildren: 0.1,
				delayChildren: 0.1,
			},
		},
	};

	const cardVariants = {
		hidden: {
			opacity: 0,
			y: 30,
		},
		visible: {
			opacity: 1,
			y: 0,
			transition: {
				duration: 0.5,
				ease: "easeOut",
			},
		},
	};

	if (loading) {
		return (
			<div className="max-w-6xl mx-auto px-4 py-8 flex justify-center items-center">
				{/* Loading Animation with Framer Motion */}
				<motion.div
					className="w-10 h-10 border-4 border-t-transparent border-steerOrange rounded-full animate-spin"
					initial={{ rotate: 0 }}
					animate={{ rotate: 360 }}
					transition={{
						repeat: Infinity,
						duration: 1,
						ease: "linear",
					}}
				/>
			</div>
		);
	}

	if (error) {
		return (
			<div className="max-w-6xl mx-auto px-4 py-8">
				<div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
					<h2 className="text-xl font-semibold text-red-800 mb-2">
						Error Loading Posts
					</h2>
					<p className="text-red-600 mb-4">{error}</p>
					<Button
						buttonName="Try Again"
						buttonStyle="primary"
						onClick={fetchPosts}
					/>
				</div>
			</div>
		);
	}

	// Use filtered posts or all posts based on numberOfPosts prop
	const displayedPosts = numberOfPosts
		? filteredPosts.slice(0, numberOfPosts)
		: filteredPosts;

	return (
		<div className="flex flex-col gap-4">
			{showMetaTags && (
				<Helmet>
					<title>Driving Tips & News Blog | Steer Smart Driving School</title>
					<meta
						name="description"
						content="Read the latest driving tips, road safety advice, and news from Steer Smart Driving School. Expert insights to help you become a safer, more confident driver."
					/>
					<link rel="canonical" href="https://steersmartdriving.com.au/blogs" />
				</Helmet>
			)}
			{/* Tag Filter Display */}
			{activeTag && (
				<div className="flex justify-between items-center flex-col lg:flex-row">
					<div className="flex flex-col lg:flex-row items-center gap-4">
						<div className="flex items-center gap-2">
							<span className="text-gray-700 font-medium">
								Filtering by tag:
							</span>
							<span className="px-3 py-1 bg-steerOrange text-white text-sm rounded-full font-medium">
								#{activeTag}
							</span>
						</div>
						<button
							onClick={clearTagFilter}
							className="flex items-center gap-1 text-gray-600 hover:text-gray-800 transition-colors"
						>
							<X className="w-4 h-4" />
							<span className="text-sm">Clear filter</span>
						</button>
					</div>
					<p className="text-sm text-gray-600 mt-2">
						Showing {displayedPosts.length} post
						{displayedPosts.length !== 1 ? "s" : ""}
						{numberOfPosts &&
							filteredPosts.length > numberOfPosts &&
							` (${filteredPosts.length} total)`}
					</p>
				</div>
			)}

			<motion.div
				className="grid gap-8 md:grid-cols-2 lg:grid-cols-4"
				variants={containerVariants}
				initial="hidden"
				animate="visible"
				key={`${activeTag}-${displayedPosts.length}`} // Re-trigger animation when posts change
			>
				{displayedPosts.map((post) => (
					<Link key={post.id} to={`/articles/${post.slug}`} className="">
						<motion.article
							className="gap-2 rounded-2xl bg-white/60 backdrop-blur-md p-2 overflow-hidden cursor-pointer flex flex-col group"
							variants={cardVariants}
							layout
						>
							<div className="w-full aspect-5/3 flex items-center justify-center overflow-hidden">
								{getFeaturedImage(post) ? (
									<motion.img
										src={getFeaturedImage(post)}
										alt={stripHTML(post.title.rendered)}
										className="w-full h-full object-cover rounded-2xl"
										initial={{ scale: 1.1 }}
										animate={{ scale: 1 }}
										transition={{ duration: 0.6, ease: "easeOut" }}
									/>
								) : (
									<motion.img
										src="/images/image5.webp"
										alt={stripHTML(post.title.rendered)}
										className="w-full h-full object-cover rounded-2xl"
										initial={{ scale: 1.1 }}
										animate={{ scale: 1 }}
										transition={{ duration: 0.6, ease: "easeOut" }}
									/>
								)}
							</div>

							<div className="flex flex-col flex-1 p-4 bg-steerBg rounded-2xl">
								<motion.h2
									className="text-sm font-semibold text-steerBlack mb-2 line-clamp-2"
									initial={{ opacity: 0, y: 10 }}
									animate={{ opacity: 1, y: 0 }}
									transition={{ delay: 0.2, duration: 0.4 }}
								>
									{stripHTML(post.title.rendered)}
								</motion.h2>

								<motion.p
									className=" text-xs mb-4 line-clamp-2"
									initial={{ opacity: 0, y: 10 }}
									animate={{ opacity: 1, y: 0 }}
									transition={{ delay: 0.3, duration: 0.4 }}
								>
									{stripHTML(post.excerpt.rendered)}
								</motion.p>

								{/* Display tags for each post */}
								{/* {getTags(post).length > 0 && (
								<div className="flex flex-wrap gap-1 mb-4">
									{getTags(post)
										.slice(0, 3)
										.map((tag) => (
											<Link
												key={tag.id}
												to={`/blogs?tag=${encodeURIComponent(tag.name)}`}
												className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full hover:bg-orange-200 transition-colors"
											>
												#{tag.name}
											</Link>
										))}
									{getTags(post).length > 3 && (
										<span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
											+{getTags(post).length - 3}
										</span>
									)}
								</div>
							)} */}

								<motion.div
									className="flex items-center justify-between pt-2 gap-2"
									initial={{ opacity: 0, y: 10 }}
									animate={{ opacity: 1, y: 0 }}
									transition={{ delay: 0.4, duration: 0.4 }}
								>
									<time className="text-xs whitespace-nowrap text-steerBlack">
										{formatDate(post.date)}
									</time>
									<span className="inline-flex items-center font-medium text-sm gap-2 text-steerOrange">
										<IoChevronForward className="group-hover:-mr-2 duration-300 transition-all ease-in" />
										Read More
									</span>
								</motion.div>
							</div>
						</motion.article>
					</Link>
				))}
			</motion.div>

			{/* No posts found state */}
			{displayedPosts.length === 0 && !loading && (
				<motion.div
					className="text-center py-12"
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.5 }}
				>
					{activeTag ? (
						<>
							<h2 className="text-2xl font-semibold text-gray-900 mb-2">
								No posts found with tag &quot;#{activeTag}&quot;
							</h2>
							<p className="text-gray-600 mb-4">
								Try browsing all posts or select a different tag.
							</p>
							<Button
								buttonName="Show All Posts"
								buttonStyle="primary"
								onClick={clearTagFilter}
							/>
						</>
					) : (
						<>
							<h2 className="text-2xl font-semibold text-gray-900 mb-2">
								No posts found
							</h2>
							<p className="text-gray-600">Check back later for new content!</p>
						</>
					)}
				</motion.div>
			)}

			{/* Show "View All" link only when not filtering and numberOfPosts is limited */}
			{numberOfPosts && (
				<motion.div
					className="text-center py-12 flex justify-center"
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ delay: 0.3, duration: 0.5 }}
				>
					<Button
						buttonName="View All Posts"
						buttonStyle="primary"
						buttonLink="/articles"
					/>
				</motion.div>
			)}
		</div>
	);
}

Blogs.propTypes = {
	numberOfPosts: PropTypes.number,
	showMetaTags: PropTypes.bool,
};
